(() => {
  if (window.Framewerk?.version) return;

  const VERSION = "2.0.4";
  const KEYS = Object.freeze({
    vehicle: "framewerk:vehicle:v1",
    garage: "framewerk:garage:v1",
    comparison: "framewerk:comparison:v1",
    quote: "framewerk:quote:v1",
  });
  const EVENTS = Object.freeze({
    ready: "framewerk:ready",
    vehicleChanged: "framewerk:vehicle:changed",
    garageChanged: "framewerk:garage:changed",
    comparisonChanged: "framewerk:comparison:changed",
    quoteChanged: "framewerk:quote:changed",
    sectionRendered: "framewerk:lifecycle:section-rendered",
    catalogueState: "framewerk:catalogue:state",
  });
  const CATALOGUE_FILTER_SUFFIX =
    ".framewerk_catalogue_spike.compatibility_tokens";
  const CATALOGUE_FILTER_PARAM =
    "filter.p.m.framewerk_catalogue_spike.compatibility_tokens";
  const CATALOGUE_VEHICLE_PREFIX = "vehicle:";
  const CATALOGUE_UNIVERSAL_TOKEN = "universal";
  const VEHICLE_CACHE_KEY = "framewerk:vehicle-catalogue:v1";
  const VEHICLE_CACHE_TTL_MS = 5 * 60 * 1000;
  const VEHICLE_REQUEST_KEY = "__framewerkVehicleCatalogueRequest";

  const listeners = new Map();
  const legacyVehicle = read(
    "framewerk:mister-fitment:selected-vehicle",
    null,
  );
  const state = {
    vehicle: read(KEYS.vehicle, legacyVehicle),
    garage: read(KEYS.garage, []),
    comparison: read(KEYS.comparison, []),
    quote: read(KEYS.quote, []),
  };

  function read(key, fallback) {
    try {
      const value = JSON.parse(localStorage.getItem(key));
      return value ?? fallback;
    } catch {
      return fallback;
    }
  }

  function write(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.error("Framewerk storage write failed", { key, error });
      return false;
    }
  }

  function notifyListener(event, listener, detail) {
    try {
      listener(detail);
    } catch (error) {
      console.error("Framewerk event listener failed", { event, error });
    }
  }

  function publish(event, detail) {
    for (const listener of listeners.get(event) ?? []) {
      notifyListener(event, listener, detail);
    }
    window.dispatchEvent(new CustomEvent(event, { detail }));
  }

  function subscribe(event, listener, options = {}) {
    const group = listeners.get(event) ?? new Set();
    group.add(listener);
    listeners.set(event, group);
    if (options.replayCurrent) {
      if (event === EVENTS.vehicleChanged) {
        notifyListener(event, listener, state.vehicle);
      }
      if (event === EVENTS.garageChanged) {
        notifyListener(event, listener, [...state.garage]);
      }
      if (event === EVENTS.comparisonChanged) {
        notifyListener(event, listener, [...state.comparison]);
      }
      if (event === EVENTS.quoteChanged) {
        notifyListener(event, listener, [...state.quote]);
      }
    }
    return () => group.delete(listener);
  }

  function stableVehicle(vehicle) {
    if (!vehicle || typeof vehicle !== "object") return null;
    const code = String(vehicle.vehicleCode ?? vehicle.code ?? "").trim();
    if (!code) throw new Error("Framewerk vehicle requires a permanent code.");
    return {
      id: vehicle.id ?? null,
      key: vehicle.key ?? null,
      code,
      vehicleCode: code,
      make: String(vehicle.make ?? ""),
      model: String(vehicle.model ?? ""),
      series: String(vehicle.series ?? ""),
      variant: String(vehicle.variant ?? ""),
      yearFrom: vehicle.yearFrom ?? null,
      yearTo: vehicle.yearTo ?? null,
      landingPageUrl: vehicle.landingPageUrl ?? null,
      bannerImageUrl: vehicle.bannerImageUrl ?? null,
      label: String(
        vehicle.label ??
          [vehicle.make, vehicle.model, vehicle.series, vehicle.variant]
            .filter(Boolean)
            .join(" "),
      ),
    };
  }

  function vehicleIdentity(vehicle) {
    return [vehicle?.make, vehicle?.model, vehicle?.series, vehicle?.variant]
      .map((value) => String(value ?? "").trim().toLowerCase())
      .join("|");
  }

  function readVehicleCatalogueCache() {
    try {
      const cached = JSON.parse(window.sessionStorage?.getItem(VEHICLE_CACHE_KEY));
      if (
        !cached ||
        typeof cached.storedAt !== "number" ||
        Date.now() - cached.storedAt > VEHICLE_CACHE_TTL_MS ||
        !cached.data ||
        !Array.isArray(cached.data.vehicles)
      ) {
        return null;
      }
      return cached.data;
    } catch {
      return null;
    }
  }

  function writeVehicleCatalogueCache(data) {
    if (!data || !Array.isArray(data.vehicles)) return;
    try {
      window.sessionStorage?.setItem(
        VEHICLE_CACHE_KEY,
        JSON.stringify({ storedAt: Date.now(), data }),
      );
    } catch {
      // Session storage can be unavailable in privacy-restricted storefronts.
      return undefined;
    }
  }

  function vehicleCatalogueRequest() {
    const pending = window[VEHICLE_REQUEST_KEY];
    if (pending && typeof pending.then === "function") return pending;

    let request;
    request = fetch("/apps/framewerk?resource=vehicles", {
      cache: "no-store",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then(async (response) => {
        if (!response.ok) return null;
        const data = await response.json();
        writeVehicleCatalogueCache(data);
        return data;
      })
      .finally(() => {
        if (window[VEHICLE_REQUEST_KEY] === request) {
          delete window[VEHICLE_REQUEST_KEY];
        }
      });
    window[VEHICLE_REQUEST_KEY] = request;
    return request;
  }

  async function reconcileStoredVehicle() {
    if (!state.vehicle) return;
    try {
      let data = readVehicleCatalogueCache();
      if (!data) {
        data = await vehicleCatalogueRequest();
        if (!data) return;
      }
      const vehicles = Array.isArray(data.vehicles) ? data.vehicles : [];
      if (!vehicles.length) return;

      const identity = vehicleIdentity(state.vehicle);
      const hasSemanticIdentity = identity.replaceAll("|", "").length > 0;
      const semanticMatch = hasSemanticIdentity
        ? vehicles.find((vehicle) => vehicleIdentity(vehicle) === identity)
        : null;
      const codeMatch = vehicles.find(
        (vehicle) =>
          String(vehicle.code ?? vehicle.vehicleCode ?? "") ===
          String(state.vehicle.code ?? ""),
      );
      const canonical = semanticMatch || codeMatch || null;

      if (!canonical) {
        state.vehicle = null;
        write(KEYS.vehicle, null);
        write("framewerk:mister-fitment:selected-vehicle", null);
        return;
      }

      const previousCode = String(state.vehicle.code ?? "");
      state.vehicle = stableVehicle(canonical);
      write(KEYS.vehicle, state.vehicle);
      write("framewerk:mister-fitment:selected-vehicle", {
        ...canonical,
        label: state.vehicle.label,
      });
      if (previousCode !== state.vehicle.code) {
        console.info("Framewerk corrected a stale vehicle code", {
          previousCode,
          vehicleCode: state.vehicle.code,
          vehicle: state.vehicle.label,
        });
      }
    } catch (error) {
      console.warn("Framewerk could not verify the stored vehicle", error);
    }
  }

  function stableProduct(product) {
    if (typeof product === "string") return { handle: product };
    if (!product || typeof product !== "object") {
      throw new Error("Framewerk product is required.");
    }
    const handle = String(product.handle ?? "").trim();
    if (!handle) throw new Error("Framewerk product requires a handle.");
    return {
      handle,
      productId: product.productId ? String(product.productId) : null,
      variantId: product.variantId ? String(product.variantId) : null,
      title: String(product.title ?? ""),
      url: String(product.url ?? `/products/${handle}`),
      image: String(product.image ?? ""),
      quantity: Math.max(1, Number(product.quantity) || 1),
    };
  }

  function isManagedCatalogueToken(value) {
    return (
      value === CATALOGUE_UNIVERSAL_TOKEN ||
      value.startsWith(CATALOGUE_VEHICLE_PREFIX)
    );
  }

  function findCatalogueFilterParam(url = new URL(window.location.href)) {
    for (const key of url.searchParams.keys()) {
      if (key.endsWith(CATALOGUE_FILTER_SUFFIX)) return key;
    }
    const input = document.querySelector?.(
      `input[name$="${CATALOGUE_FILTER_SUFFIX}"]`,
    );
    const root = document.querySelector?.("[data-mf-catalogue-spike]");
    return root?.dataset?.filterParam || input?.name || CATALOGUE_FILTER_PARAM;
  }

  function buildCatalogueUrl(vehicle, currentHref = window.location.href) {
    const url = new URL(currentHref, window.location.origin);
    const filterParam = findCatalogueFilterParam(url);
    const code = vehicle?.code ? String(vehicle.code).trim() : null;
    const legacyParams = [...url.searchParams.keys()].filter((key) => {
      const normalised = key.toLowerCase();
      return (
        normalised.startsWith("filter.p.m.") &&
        (normalised.includes("compatible_vehicles") ||
          normalised.includes("compatible-vehicles"))
      );
    });
    legacyParams.forEach((key) => url.searchParams.delete(key));

    const current = url.searchParams.getAll(filterParam);
    const preserved = current.filter((value) => !isManagedCatalogueToken(value));
    const desired = code
      ? [
          ...preserved,
          `${CATALOGUE_VEHICLE_PREFIX}${code}`,
        ]
      : preserved;
    const uniqueDesired = [...new Set(desired)];
    const changed =
      legacyParams.length > 0 ||
      JSON.stringify([...new Set(current)].sort()) !==
        JSON.stringify([...uniqueDesired].sort());

    url.searchParams.delete(filterParam);
    uniqueDesired.forEach((value) => url.searchParams.append(filterParam, value));
    if (changed) url.searchParams.delete("page");
    return { url, changed, filterParam, code };
  }

  function isCataloguePage() {
    const pathname = String(window.location?.pathname ?? "");
    return (
      pathname.startsWith("/collections/") ||
      pathname === "/collections" ||
      pathname.startsWith("/search")
    );
  }

  function catalogueFacetController() {
    const forms = [
      ...(document.querySelectorAll?.("facets-form-component[section-id]") ?? []),
    ];
    return (
      forms.find((form) => !form.closest?.("dialog") && form.isConnected) ||
      forms.find((form) => form.isConnected) ||
      null
    );
  }

  function setCatalogueState(status, message = "", detail = {}) {
    document.querySelectorAll?.("[data-mf-catalogue-spike]").forEach((root) => {
      root.dataset.state = status;
      root.dataset.ready = "true";
      const output = root.querySelector?.("[data-mf-catalogue-spike-status]");
      if (output) {
        output.hidden = !message;
        output.textContent = message;
      }
    });
    if (document.documentElement?.dataset) {
      document.documentElement.dataset.mfCatalogueProjectionState =
        status.toUpperCase();
    }
    publish(EVENTS.catalogueState, { status, message, ...detail });
  }

  let catalogueBusy = false;
  let catalogueQueued = false;
  let catalogueQueuedVehicle = null;
  let catalogueQueuedTarget = null;
  let catalogueActiveTarget = null;

  async function waitForCatalogueFacetController(timeoutMs = 800) {
    let facets = catalogueFacetController();
    if (typeof facets?.updateFiltersByURL === "function") return facets;

    const startedAt = Date.now();
    if (window.customElements?.whenDefined) {
      await Promise.race([
        window.customElements.whenDefined("facets-form-component"),
        new Promise((resolve) => window.setTimeout(resolve, 250)),
      ]);
    }

    while (Date.now() - startedAt < timeoutMs) {
      facets = catalogueFacetController();
      if (typeof facets?.updateFiltersByURL === "function") return facets;
      await new Promise((resolve) => window.setTimeout(resolve, 50));
    }
    return catalogueFacetController();
  }

  async function applyCatalogueVehicle(vehicle) {
    if (!isCataloguePage()) return { changed: false, skipped: true };
    if (vehicle && !String(vehicle.code ?? "").trim()) {
      setCatalogueState(
        "failed",
        "Compatibility filtering is unavailable because this vehicle has no permanent code.",
      );
      return { changed: false, failed: true };
    }

    const result = buildCatalogueUrl(vehicle);
    if (!result.changed) {
      setCatalogueState("current", "", { url: result.url.toString() });
      return result;
    }

    // The active controller is normally ready immediately. On first load, use
    // a short bounded wait so a late custom-element upgrade does not force a
    // premature full-page navigation or hold the storefront for two seconds.
    const facets = await waitForCatalogueFacetController();

    setCatalogueState("updating", "Updating compatible products…", {
      url: result.url.toString(),
    });
    if (typeof facets?.updateFiltersByURL !== "function") {
      window.location.assign(result.url.toString());
      return result;
    }

    try {
      await facets.updateFiltersByURL(result.url.toString());
      setCatalogueState("current", "", { url: result.url.toString() });
      return result;
    } catch (error) {
      setCatalogueState(
        "failed",
        "Shopify could not refresh compatible products. Retry or clear the selected vehicle.",
        { error },
      );
      console.error("Framewerk catalogue filtering failed", error);
      return { ...result, failed: true };
    }
  }

  async function flushCatalogueVehicle() {
    if (catalogueBusy) return;
    catalogueBusy = true;
    try {
      while (catalogueQueued) {
        const vehicle = catalogueQueuedVehicle;
        catalogueActiveTarget = catalogueQueuedTarget;
        catalogueQueued = false;
        catalogueQueuedTarget = null;
        await applyCatalogueVehicle(vehicle);
      }
    } finally {
      catalogueBusy = false;
      catalogueActiveTarget = null;
    }
  }

  function queueCatalogueVehicle(vehicle) {
    const target = isCataloguePage()
      ? buildCatalogueUrl(vehicle).url.toString()
      : null;
    if (
      target &&
      (target === catalogueActiveTarget ||
        (catalogueQueued && target === catalogueQueuedTarget))
    ) {
      return;
    }
    catalogueQueuedVehicle = vehicle;
    catalogueQueuedTarget = target;
    catalogueQueued = true;
    void flushCatalogueVehicle();
  }

  function collection(name, key, event, limit) {
    return Object.freeze({
      get: () => [...state[name]],
      has: (handle) => state[name].some((item) => item.handle === handle),
      add(product) {
        const item = stableProduct(product);
        if (this.has(item.handle)) return this.get();
        if (state[name].length >= limit) {
          throw new Error(`Framewerk ${name} limit of ${limit} reached.`);
        }
        state[name] = [...state[name], item];
        write(key, state[name]);
        publish(event, this.get());
        return this.get();
      },
      remove(handle) {
        state[name] = state[name].filter((item) => item.handle !== handle);
        write(key, state[name]);
        publish(event, this.get());
        return this.get();
      },
      update(handle, changes) {
        state[name] = state[name].map((item) =>
          item.handle === handle ? stableProduct({ ...item, ...changes }) : item,
        );
        write(key, state[name]);
        publish(event, this.get());
        return this.get();
      },
      clear() {
        state[name] = [];
        write(key, state[name]);
        publish(event, []);
      },
      subscribe: (listener, options) => subscribe(event, listener, options),
      limit,
    });
  }

  function updateVehiclePresentation(root = document) {
    const vehicle = state.vehicle;
    if (document.body?.dataset) {
      document.body.dataset.activeVehicle = vehicle ? "true" : "false";
    }
    root.querySelectorAll?.("[data-active-vehicle-label]").forEach((element) => {
      if (!vehicle) {
        element.textContent = element.dataset.activeVehicleLabelDefault ?? "";
        return;
      }

      const labelMode =
        element.dataset.mfSelectedLabelMode ||
        element.closest?.("[data-mf-selected-label-mode]")?.dataset
          ?.mfSelectedLabelMode;
      if (labelMode === "series_model") {
        element.textContent = [vehicle.series, vehicle.model]
          .map((value) => String(value ?? "").trim())
          .filter(Boolean)
          .join(" ") || vehicle.label;
        return;
      }

      element.textContent = vehicle.label;
    });
    root
      .querySelectorAll?.(
        "add-to-cart-component [data-force-vehicle-selection], [data-force-vehicle-selection]",
      )
      .forEach((container) => {
        if (container.dataset.canAddToCart !== "true") return;
        const button =
          container.matches?.("button") && container.type === "submit"
            ? container
            : container.querySelector?.(
                '[ref="addToCartButton"], button[type="submit"]',
              );
        if (button) button.disabled = !vehicle;
      });
  }

  function mountRuntime(root = document) {
    updateVehiclePresentation(root);
    root.querySelectorAll?.("[data-framewerk-comparison]").forEach(
      mountComparison,
    );
    root.querySelectorAll?.("[data-framewerk-comparison-toggle]").forEach(
      mountComparisonToggle,
    );
    root.querySelectorAll?.("[data-framewerk-quote-add]").forEach(
      mountQuoteButton,
    );
    root.querySelectorAll?.("[data-framewerk-quote-form]").forEach(
      mountQuoteForm,
    );
    updateQuoteCounters(root);
  }

  function updateQuoteCounters(root = document) {
    const count = api.quoteRequest
      .get()
      .reduce((total, item) => total + item.quantity, 0);
    root.querySelectorAll?.("[data-framewerk-quote-count]").forEach((node) => {
      node.textContent = String(count);
      node.hidden = count === 0;
    });
  }

  function mountComparisonToggle(button) {
    if (button.dataset.framewerkMounted === "true") return;
    button.dataset.framewerkMounted = "true";
    const handle = button.dataset.productHandle;
    if (!handle) {
      button.disabled = true;
      return;
    }

    button.addEventListener("click", () => {
      if (api.comparison.has(handle)) {
        api.comparison.remove(handle);
      } else {
        api.comparison.add({
          handle,
          productId: button.dataset.productId,
          variantId: button.dataset.variantId,
          title: button.dataset.productTitle,
          url: button.dataset.productUrl,
          image: button.dataset.productImage,
        });
      }
    });

    const render = (items) => {
      const selected = items.some((item) => item.handle === handle);
      button.setAttribute("aria-pressed", String(selected));
      button.disabled = items.length >= api.comparison.limit && !selected;
      button.textContent = selected
        ? button.dataset.removeLabel ?? "Remove from comparison"
        : button.dataset.addLabel ?? "Compare";
    };
    api.comparison.subscribe(render, { replayCurrent: true });
  }

  function mountQuoteButton(button) {
    if (button.dataset.framewerkMounted === "true") return;
    button.dataset.framewerkMounted = "true";
    const handle = button.dataset.productHandle;
    if (!handle) return;
    button.addEventListener("click", () => {
      if (api.quoteRequest.has(handle)) {
        api.quoteRequest.remove(handle);
      } else {
        api.quoteRequest.add({
          handle,
          productId: button.dataset.productId,
          variantId: button.dataset.variantId,
          title: button.dataset.productTitle,
          quantity: 1,
        });
      }
    });
    const render = (items) => {
      const selected = items.some((item) => item.handle === handle);
      button.setAttribute("aria-pressed", String(selected));
      button.textContent = selected
        ? button.dataset.removeLabel ?? "Remove from quote"
        : button.dataset.addLabel ?? "Request a quote";
    };
    api.quoteRequest.subscribe(render, { replayCurrent: true });
  }

  function mountQuoteForm(form) {
    if (form.dataset.framewerkMounted === "true") return;
    form.dataset.framewerkMounted = "true";
    const status = form.querySelector("[data-framewerk-quote-status]");
    const submit = form.querySelector('button[type="submit"]');
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      const items = api.quoteRequest.get();
      if (!items.length) {
        if (status) status.textContent = "Add at least one product first.";
        return;
      }
      if (submit) submit.disabled = true;
      if (status) status.textContent = "Submitting quote request…";
      const fields = new FormData(form);
      try {
        const response = await fetch("/apps/framewerk?resource=quote", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            customerName: fields.get("customerName"),
            email: fields.get("email"),
            phone: fields.get("phone"),
            message: fields.get("message"),
            vehicleCode: api.vehicle.get()?.code ?? null,
            items,
          }),
        });
        const body = await response.json();
        if (!response.ok) throw new Error(body.error ?? "Quote submission failed.");
        api.quoteRequest.clear();
        form.reset();
        if (status) {
          status.textContent = `Quote request #${body.quoteRequest.id} was submitted.`;
        }
      } catch (error) {
        if (status) {
          status.textContent =
            error instanceof Error ? error.message : "Quote submission failed.";
        }
      } finally {
        if (submit) submit.disabled = false;
      }
    });
  }

  async function loadProduct(handle) {
    const response = await fetch(`/products/${encodeURIComponent(handle)}.js`);
    if (!response.ok) throw new Error(`Product ${handle} could not be loaded.`);
    return response.json();
  }

  function element(tag, text, className) {
    const node = document.createElement(tag);
    if (text) node.textContent = text;
    if (className) node.className = className;
    return node;
  }

  async function mountComparison(root) {
    if (root.dataset.framewerkMounted === "true") return;
    root.dataset.framewerkMounted = "true";
    const status = root.querySelector("[data-framewerk-comparison-status]");
    const grid = root.querySelector("[data-framewerk-comparison-grid]");

    async function render(items = api.comparison.get()) {
      if (!grid) return;
      grid.replaceChildren();
      if (!items.length) {
        if (status) status.textContent = "Choose products to compare.";
        return;
      }
      if (status) status.textContent = "Loading comparison…";
      const results = await Promise.allSettled(
        items.map((item) => loadProduct(item.handle)),
      );
      results.forEach((result, index) => {
        const saved = items[index];
        if (result.status !== "fulfilled") return;
        const product = result.value;
        const card = element("article", "", "framewerk-comparison__product");
        const title = element("a", product.title, "framewerk-comparison__title");
        title.href = product.url || `/products/${saved.handle}`;
        card.append(title);
        const variants = element("select", "", "framewerk-comparison__variants");
        for (const variant of product.variants ?? []) {
          const option = element("option", variant.title);
          option.value = String(variant.id);
          option.selected =
            String(saved.variantId ?? product.variants?.[0]?.id) === option.value;
          variants.append(option);
        }
        variants.addEventListener("change", () =>
          api.comparison.update(saved.handle, { variantId: variants.value }),
        );
        card.append(variants);
        const add = element("button", "Add to cart");
        add.type = "button";
        add.addEventListener("click", async () => {
          add.disabled = true;
          try {
            const response = await fetch("/cart/add.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ items: [{ id: variants.value, quantity: 1 }] }),
            });
            if (!response.ok) throw new Error("Add to cart failed.");
            publish("framewerk:comparison:added-to-cart", {
              handle: saved.handle,
              variantId: variants.value,
            });
          } finally {
            add.disabled = false;
          }
        });
        card.append(add);
        const remove = element("button", "Remove");
        remove.type = "button";
        remove.addEventListener("click", () =>
          api.comparison.remove(saved.handle),
        );
        card.append(remove);
        grid.append(card);
      });
      if (status) {
        const failed = results.filter((result) => result.status === "rejected").length;
        status.textContent = failed
          ? `${failed} comparison product${failed === 1 ? "" : "s"} could not be loaded.`
          : `${items.length} product${items.length === 1 ? "" : "s"} selected.`;
      }
    }

    const unsubscribe = api.comparison.subscribe(render, {
      replayCurrent: true,
    });
    root.framewerkUnmount = unsubscribe;
  }

  let readyResolve;
  const ready = new Promise((resolve) => {
    readyResolve = resolve;
  });

  const api = Object.freeze({
    version: VERSION,
    ready,
    events: EVENTS,
    storageKeys: KEYS,
    vehicle: Object.freeze({
      get: () => state.vehicle,
      select(vehicle) {
        const nextVehicle = stableVehicle(vehicle);
        if (JSON.stringify(nextVehicle) === JSON.stringify(state.vehicle)) {
          return state.vehicle;
        }
        state.vehicle = nextVehicle;
        write(KEYS.vehicle, state.vehicle);
        publish(EVENTS.vehicleChanged, state.vehicle);
        return state.vehicle;
      },
      clear() {
        if (!state.vehicle) return null;
        state.vehicle = null;
        write(KEYS.vehicle, null);
        publish(EVENTS.vehicleChanged, null);
        return null;
      },
      subscribe: (listener, options) =>
        subscribe(EVENTS.vehicleChanged, listener, options),
    }),
    garage: Object.freeze({
      get: () => [...state.garage],
      replace(vehicles) {
        const nextGarage = [];
        const seenCodes = new Set();
        for (const vehicle of Array.isArray(vehicles) ? vehicles : []) {
          let item;
          try {
            item = stableVehicle(vehicle);
          } catch {
            continue;
          }
          if (!item || seenCodes.has(item.code)) continue;
          seenCodes.add(item.code);
          nextGarage.push(item);
          if (nextGarage.length === 10) break;
        }
        if (JSON.stringify(nextGarage) === JSON.stringify(state.garage)) {
          return [...state.garage];
        }
        state.garage = nextGarage;
        write(KEYS.garage, state.garage);
        publish(EVENTS.garageChanged, [...state.garage]);
        return [...state.garage];
      },
      add(vehicle) {
        const item = stableVehicle(vehicle);
        state.garage = [
          item,
          ...state.garage.filter((saved) => saved.code !== item.code),
        ].slice(0, 10);
        write(KEYS.garage, state.garage);
        publish(EVENTS.garageChanged, [...state.garage]);
        return [...state.garage];
      },
      remove(code) {
        const nextGarage = state.garage.filter(
          (vehicle) => vehicle.code !== code,
        );
        if (nextGarage.length === state.garage.length) {
          return [...state.garage];
        }
        state.garage = nextGarage;
        write(KEYS.garage, state.garage);
        publish(EVENTS.garageChanged, [...state.garage]);
        return [...state.garage];
      },
      subscribe: (listener, options) =>
        subscribe(EVENTS.garageChanged, listener, options),
    }),
    comparison: collection(
      "comparison",
      KEYS.comparison,
      EVENTS.comparisonChanged,
      4,
    ),
    quoteRequest: collection("quote", KEYS.quote, EVENTS.quoteChanged, 50),
    compatibility: Object.freeze({
      async getProductCompatibility(productId, vehicle = state.vehicle) {
        if (!vehicle) return { productId, vehicleCode: null, status: "unselected" };
        const url = new URL("/apps/framewerk", window.location.origin);
        url.searchParams.set("resource", "fitment");
        url.searchParams.set("product_id", String(productId));
        url.searchParams.set("vehicle_code", String(vehicle.code));
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Compatibility request failed (${response.status}).`);
        return response.json();
      },
    }),
    catalogue: Object.freeze({
      refresh() {
        queueCatalogueVehicle(state.vehicle);
      },
      clearVehicleConstraint() {
        api.vehicle.clear();
      },
      buildUrl(vehicle, currentHref) {
        return buildCatalogueUrl(vehicle, currentHref).url.toString();
      },
    }),
    lifecycle: Object.freeze({
      mount(root = document) {
        mountRuntime(root);
        root.dispatchEvent(new CustomEvent(EVENTS.ready, { bubbles: true }));
      },
      unmount() {},
      afterSectionRender(root = document) {
        publish(EVENTS.sectionRendered, root);
        api.lifecycle.mount(root);
        queueCatalogueVehicle(state.vehicle);
      },
    }),
    subscribe,
  });

  window.Framewerk = api;
  subscribe(EVENTS.vehicleChanged, () => updateVehiclePresentation(document));
  subscribe(EVENTS.vehicleChanged, queueCatalogueVehicle);
  subscribe(EVENTS.quoteChanged, () => updateQuoteCounters(document));
  document.addEventListener?.("shopify:section:load", (event) => {
    const root = event.target instanceof HTMLElement ? event.target : document;
    api.lifecycle.afterSectionRender(root);
  });
  window.addEventListener?.("pageshow", () => api.lifecycle.mount(document));
  readyResolve();
  publish(EVENTS.ready, api);
  queueMicrotask(async () => {
    await reconcileStoredVehicle();
    window.dispatchEvent(
      new CustomEvent("framewerk:mister-fitment:vehicle-changed", {
        detail: state.vehicle,
      }),
    );
    publish(EVENTS.vehicleChanged, state.vehicle);
    publish(EVENTS.garageChanged, [...state.garage]);
    publish(EVENTS.comparisonChanged, [...state.comparison]);
    publish(EVENTS.quoteChanged, [...state.quote]);
    api.lifecycle.mount(document);
  });
})();
